/*
Purpose: Top toolbar for casefile actions in file explorer style.
Audience: Case owners and collaborators.
What it does:
- Displays all casefile actions (Back, Upload, Edit, Make Public, Delete, Results) in a horizontal toolbar
- Provides consistent Windows file explorer-style interface
- Mobile-optimized: Shows icon-only buttons and groups secondary actions in a menu
Interplay:
- Used by CasefileDetail component to replace sidebar actions
*/
import React, { useState, useRef, useEffect } from 'react';
import { FaArrowLeft, FaUpload, FaEdit, FaGlobe, FaTrashAlt, FaRobot, FaEllipsisV } from 'react-icons/fa';
import './CasefileToolbar.css';

const CasefileToolbar = ({
  isOwner,
  isPublic,
  onBack,
  onUpload,
  onEdit,
  onPublicToggle,
  onDelete,
  onResults,
  onToggleNavigation,
  showNavigationToggle = true
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Primary actions (always visible on mobile)
  const primaryActions = [
    {
      id: 'back',
      label: 'Back',
      icon: FaArrowLeft,
      onClick: onBack,
      show: true,
      className: 'toolbar-back',
      primary: true
    },
    {
      id: 'upload',
      label: 'Upload',
      icon: FaUpload,
      onClick: onUpload,
      show: isOwner,
      className: 'toolbar-upload',
      primary: true
    },
    {
      id: 'results',
      label: 'Results',
      icon: FaRobot,
      onClick: onResults,
      show: true,
      className: 'toolbar-results',
      primary: true
    }
  ];

  // Secondary actions (in mobile menu)
  const secondaryActions = [
    {
      id: 'edit',
      label: 'Edit Casefile',
      icon: FaEdit,
      onClick: () => { onEdit(); setIsMenuOpen(false); },
      show: isOwner,
      className: 'toolbar-edit'
    },
    {
      id: 'public',
      label: isPublic ? 'Make Private' : 'Make Public',
      icon: FaGlobe,
      onClick: () => { onPublicToggle(); setIsMenuOpen(false); },
      show: isOwner,
      className: `toolbar-public ${isPublic ? 'active' : ''}`
    },
    {
      id: 'delete',
      label: 'Delete Casefile',
      icon: FaTrashAlt,
      onClick: () => { onDelete(); setIsMenuOpen(false); },
      show: isOwner,
      className: 'toolbar-delete'
    }
  ];

  const hasSecondaryActions = secondaryActions.some(action => action.show);

  return (
    <div className="casefile-toolbar">
      <div className="toolbar-actions">
        {/* Primary actions */}
        {primaryActions.map(action => {
          if (!action.show) return null;
          
          const IconComponent = action.icon;
          
          return (
            <button
              key={action.id}
              className={`toolbar-action ${action.className || ''}`}
              onClick={action.onClick}
              title={action.label}
              aria-label={action.label}
            >
              <IconComponent className="toolbar-icon" />
              <span className="toolbar-label">{action.label}</span>
            </button>
          );
        })}

        {/* More menu for secondary actions on mobile */}
        {hasSecondaryActions && (
          <div className="toolbar-more-menu" ref={menuRef}>
            <button
              className="toolbar-action toolbar-more"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              title="More actions"
              aria-label="More actions"
            >
              <FaEllipsisV className="toolbar-icon" />
            </button>

            {isMenuOpen && (
              <div className="toolbar-dropdown">
                {secondaryActions.map(action => {
                  if (!action.show) return null;
                  
                  const IconComponent = action.icon;
                  
                  return (
                    <button
                      key={action.id}
                      className={`dropdown-item ${action.className || ''}`}
                      onClick={action.onClick}
                    >
                      <IconComponent className="dropdown-icon" />
                      <span className="dropdown-label">{action.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CasefileToolbar;
